import React, { useRef, useCallback } from 'react';
import { Search, AlertCircle } from 'lucide-react';
import { FilterButton } from '../table/FilterButton';
import { SortButton } from '../table/SortButton';
import EnrichmentProgressBar from '../ui/EnrichmentProgressBar';
import { Condition } from '../../hooks/useMultiFilter';
import type { EnrichmentProgress } from '../../types';
import { PERFORMANCE_WARNING_THRESHOLD } from '../../constants/table';

// Base columns that should not have enrichment options
const LEAD_TYPE_BASE_COLUMNS = {
  companies: ['Name', 'Description', 'URL'],
  people: ['Name', 'Description', 'Company', 'Job Title', 'URL'],
  custom: ['Name', 'Description', 'URL']
};

export interface TableToolbarProps {
  columns: Array<{ key: string; name: string; isSegment?: boolean }>;
  tableType: 'companies' | 'people' | 'custom';
  leadsCount: number;
  searchQuery: string;
  isSearchExpanded: boolean;
  enrichmentProgressData: EnrichmentProgress | null;
  enrichingCellsCount: number;
  onSearchChange: (value: string) => void;
  onSearchExpand: () => void;
  onSearchBlur: () => void;
  onFilterChange: (conditions: Condition[]) => void;
  onSortChange: (sorts: Array<{ fieldId: string; direction: 'asc' | 'desc' }>, fullMatchesFirst: boolean) => void;
}

/**
 * Table toolbar with search, filter, sort buttons and progress indicators
 */
export const TableToolbar: React.FC<TableToolbarProps> = ({
  columns,
  tableType,
  leadsCount,
  searchQuery,
  isSearchExpanded,
  enrichmentProgressData,
  enrichingCellsCount,
  onSearchChange,
  onSearchExpand,
  onSearchBlur,
  onFilterChange,
  onSortChange,
}) => {
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter columns for filter/sort dropdowns
  const getFilterableFields = useCallback(() => {
    return columns
      .filter(col => {
        // Exclude segment columns (they only show "Match" values)
        if (col.isSegment) return false;
        // Include base columns
        const baseColumns = LEAD_TYPE_BASE_COLUMNS[tableType] || [];
        if (baseColumns.includes(col.name)) return true;
        // Include enriched columns
        if (col.key.startsWith('enriched_')) return true;
        return false;
      })
      .map(col => ({ id: col.name, name: col.name }));
  }, [columns, tableType]);

  const handleSearchExpand = useCallback(() => {
    onSearchExpand();
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 50);
  }, [onSearchExpand]);

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (!searchQuery.trim()) {
        searchInputRef.current?.blur();
        onSearchBlur();
      } else {
        onSearchChange('');
      }
    }
  }, [searchQuery, onSearchChange, onSearchBlur]);

  return (
    <>
      {/* Toolbar */}
      <div className="flex-shrink-0 h-10 bg-white border-b border-gray-100">
        <div className="flex items-center justify-between h-full px-4">
          {/* Left group - Filter, Sort */}
          <div className="flex items-center gap-0.5">
            <FilterButton
              fields={getFilterableFields()}
              onFilterChange={onFilterChange}
            />
            <SortButton
              fields={getFilterableFields()}
              onSortChange={onSortChange}
            />
          </div>

          <div className="px-4">
            <EnrichmentProgressBar
              progressData={enrichmentProgressData}
              pending={enrichingCellsCount}
            />
          </div>

          {/* Right group - Collapsible Search */}
          <div className="relative flex items-center w-44">
            {/* Search icon button - always rendered, fades out when expanded */}
            <button
              onClick={handleSearchExpand}
              className={`absolute right-0 flex items-center gap-1.5 h-8 px-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-all duration-200 ${
                isSearchExpanded || searchQuery ? 'opacity-0 pointer-events-none' : 'opacity-100'
              }`}
              title="Search (Ctrl+F)"
            >
              <Search className="w-4 h-4" />
              <span className="text-[12px] font-medium">Search</span>
            </button>

            {/* Expanded search input */}
            <div
              className={`absolute right-0 transition-all duration-200 ease-out ${
                isSearchExpanded || searchQuery
                  ? 'opacity-100 translate-x-0 pointer-events-auto'
                  : 'opacity-0 translate-x-2 pointer-events-none'
              }`}
            >
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onBlur={onSearchBlur}
                  onKeyDown={handleSearchKeyDown}
                  className="h-8 w-44 pl-8 pr-3 text-[12px] font-normal bg-gray-50 border border-gray-200 rounded-md transition-all placeholder:text-gray-400 focus:outline-none focus:bg-white focus:border-gray-300"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Warning Banner */}
      {leadsCount > PERFORMANCE_WARNING_THRESHOLD && (
        <div className="flex items-center gap-2 px-4 py-2.5 text-13 text-warning-800 bg-warning-100 border-b border-warning-500">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>
            <strong>Performance Notice:</strong> Displaying {leadsCount.toLocaleString()} rows.
            {leadsCount > 1000
              ? ' Table performance may be degraded. Consider using filters to reduce the dataset.'
              : ' Approaching performance limits. Performance may degrade above 1,000 rows.'}
          </span>
        </div>
      )}
    </>
  );
};

TableToolbar.displayName = 'TableToolbar';
